import React, {useRef, useState, useMemo} from 'react';
import {connect} from 'react-redux';
import GoogleMapReact from 'google-map-react';
import supercluster from 'points-cluster';
import SimpleMarker from './SimpleMarker';
import ClusterMarker from './ClusterMarker';
import {GOOGLE_MAP_API_KEY} from '../../../../../config';
import withGoogleMaps from '../../../../organisms/with-google-maps/withGoogleMaps';

export function GoogleMap(props) {
  const {setMapBounds, itemMap, selectedIds} = props;
  const map = useRef(null);

  const boundsChange = (boundsEvent) => {
    props.onChange(boundsEvent);
    if (setMapBounds) {
      setMapBounds(boundsEvent.bounds);
    }
  };

  const isOnPage = (points) => {
    for (let i = 0; i < points.length; i++) {
      const point = points[i];
      if (itemMap.hasOwnProperty(point.pointId)) {
        return true;
      }
    }
    return false;
  };

  const isSelected = (points) => {
    for (let i = 0; i < points.length; i++) {
      const point = points[i];
      if (selectedIds.find((id) => id === point.pointId)) {
        return true;
      }
    }
    return false;
  };

  return (
    <>
      <GoogleMapReact
        ref={(m) => (map.current = m)}
        bootstrapURLKeys={{key: GOOGLE_MAP_API_KEY}}
        options={props.options}
        hoverDistance={props.hoverDistance}
        center={props.mapProps.center}
        zoom={props.mapProps.zoom}
        onChange={boundsChange}
        onChildMouseEnter={props.onChildMouseEnter}
        onChildMouseLeave={props.onChildMouseLeave}
      >
        {props.clusters.map((markerProps) =>
          markerProps.numPoints === 1 ? (
            <SimpleMarker
              key={markerProps.markerId}
              {...markerProps}
              openFileDialog={props.openFileDialog}
              zoom={props.mapProps.zoom}
              setMapProps={props.setMapProps}
              isSelected={isSelected(markerProps.points)}
              onPage={isOnPage(markerProps.points)}
            />
          ) : (
            <ClusterMarker
              key={markerProps.markerId}
              {...markerProps}
              zoom={props.mapProps.zoom}
              setMapProps={props.setMapProps}
              isSelected={isSelected(markerProps.points)}
              onPage={isOnPage(markerProps.points)}
            />
          )
        )}
        {props.clustersHidden.map((markerProps) =>
          markerProps.numPoints === 1 ? (
            <SimpleMarker
              key={markerProps.markerId}
              {...markerProps}
              openFileDialog={props.openFileDialog}
              zoom={props.mapProps.zoom}
              setMapProps={props.setMapProps}
              isSelected={isSelected(markerProps.points)}
              onPage={isOnPage(markerProps.points)}
            />
          ) : (
            <ClusterMarker
              key={markerProps.markerId}
              {...markerProps}
              zoom={props.mapProps.zoom}
              setMapProps={props.setMapProps}
              isSelected={isSelected(markerProps.points)}
              onPage={isOnPage(markerProps.points)}
            />
          )
        )}
      </GoogleMapReact>
    </>
  );
}

const mapStateToProps = (state) => ({
  mindfile: state.mindfile,
  authenticityToken: state.auth.authenticity_token,
});

function MapView(props) {
  const {
    mindfile,
    markers = [],
    markersHidden = [],
    clusterRadius = 60,
    hoverDistance = 30,
    options = {minZoom: 1, maxZoom: 20},
  } = props;

  const initialCenter = useMemo(
    () => ({
      lat: mindfile.location?.latitude || 39.9526,
      lng: mindfile.location?.longitude || -75.1652,
    }),
    [mindfile]
  );

  const [hoveredMarkerId, setHoveredMarkerId] = useState(-1);
  const [mapProps, setMapProps] = useState({center: initialCenter, zoom: 3});

  const getCluster = useMemo(
    () =>
      supercluster(markers, {
        minZoom: options.minZoom,
        maxZoom: options.maxZoom,
        radius: clusterRadius,
      }),
    [markers, clusterRadius, options.minZoom, options.maxZoom]
  );

  const getClusterHidden = useMemo(
    () =>
      supercluster(markersHidden, {
        minZoom: options.minZoom,
        maxZoom: options.maxZoom,
        radius: clusterRadius,
      }),
    [markersHidden, clusterRadius, options.minZoom, options.maxZoom]
  );

  const clusters = useMemo(
    () =>
      mapProps.bounds
        ? getCluster(mapProps).map(({wx, wy, numPoints, points}) => ({
            lat: wy,
            lng: wx,
            text: numPoints,
            numPoints,
            pointId: points[0].pointId,
            points,
            markerId: `${numPoints}_${points[0].pointId}`,
          }))
        : [],
    [mapProps, getCluster]
  );

  const clustersHidden = useMemo(
    () =>
      mapProps.bounds
        ? getClusterHidden(mapProps).map(({wx, wy, numPoints, points}) => ({
            lat: wy,
            lng: wx,
            text: numPoints,
            numPoints,
            pointId: points[0].pointId,
            points,
            markerId: `${numPoints}_${points[0].pointId}`,
          }))
        : [],
    [mapProps, getClusterHidden]
  );

  const clustersWithHover = useMemo(
    () =>
      clusters.map((cluster) => ({
        hovered: cluster.markerId === hoveredMarkerId,
        ...cluster,
      })),
    [clusters, hoveredMarkerId]
  );

  const onChange = ({center, zoom, bounds}) => {
    setMapProps({center, zoom, bounds});
  };

  const onChildMouseEnter = (hoverKey, {markerId}) => {
    setHoveredMarkerId(markerId);
  };

  const onChildMouseLeave = () => {
    setHoveredMarkerId(-1);
  };

  return (
    <GoogleMap
      {...props}
      hoverDistance={hoverDistance}
      options={options}
      mapProps={mapProps}
      setMapProps={setMapProps}
      onChange={onChange}
      onChildMouseEnter={onChildMouseEnter}
      onChildMouseLeave={onChildMouseLeave}
      clusters={clustersWithHover}
      clustersHidden={clustersHidden}
    />
  );
}

export default connect(mapStateToProps)(withGoogleMaps(MapView));